import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, FileBarChart2, Loader2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Two manual-trigger digest cards for admins:
 *  - Daily Routing Report (24h)
 *  - Weekly Business Digest (7d)
 *
 * Auto-fires on background scheduler; admin can preview + send-now from here.
 */
export default function AdminDigestsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // "routing" | "business" | null

  const load = () => api.get("/admin/digests/preview").then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sendNow = async (kind) => {
    setBusy(kind);
    try {
      const path = kind === "routing" ? "/admin/digests/routing-report/send" : "/admin/digests/business/send";
      const { data } = await api.post(path);
      const recipientCount = (data?.recipients || []).length;
      toast.success(`${kind === "routing" ? "Routing report" : "Business digest"} sent to ${recipientCount} admin${recipientCount === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send digest");
    } finally { setBusy(null); }
  };

  const Row = ({ label, value }) => (
    <div className="flex items-baseline justify-between text-sm border-t border-slate-100 first:border-t-0 py-1.5">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono tabular-nums text-slate-900">{value}</span>
    </div>
  );

  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="admin-digests-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-start gap-3">
            <span className="inline-grid place-items-center size-9 rounded-lg bg-[#0F172A] text-white shrink-0">
              <FileBarChart2 className="size-4" strokeWidth={1.6} />
            </span>
            <div>
              <h3 className="font-medium text-slate-900 text-[15px] leading-tight">Automated digests</h3>
              <p className="text-xs text-slate-500 mt-1">Daily routing report (07:30 UTC) · Weekly business digest (Mon 07:00 UTC)</p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {/* Routing Report */}
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Daily routing</div>
              <span className="text-[10px] font-mono text-slate-400">
                {data?.routing_report?.last_run ? `Sent ${new Date(data.routing_report.last_run).toLocaleString()}` : "Not yet sent"}
              </span>
            </div>
            {data?.routing_report?.stats && (
              <div className="mb-3">
                <Row label="Leads (24h)" value={data.routing_report.stats.total_leads.toLocaleString()} />
                <Row label="Accept rate" value={data.routing_report.stats.accept_rate} />
                <Row label="Revenue" value={`£${(data.routing_report.stats.revenue || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`} />
              </div>
            )}
            <Button onClick={() => sendNow("routing")} disabled={busy === "routing"} variant="outline" size="sm" className="w-full rounded-full" data-testid="digest-send-routing">
              {busy === "routing" ? <><Loader2 className="size-3 animate-spin" /> Sending…</> : <><Mail className="size-3" /> Send routing report now</>}
            </Button>
          </div>

          {/* Business Digest */}
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Weekly business</div>
              <span className="text-[10px] font-mono text-slate-400">
                {data?.business_digest?.last_run ? `Sent ${new Date(data.business_digest.last_run).toLocaleString()}` : "Not yet sent"}
              </span>
            </div>
            {data?.business_digest?.stats && (
              <div className="mb-3">
                <Row label="Leads (7d)" value={data.business_digest.stats.total_leads.toLocaleString()} />
                <Row label="AI sessions" value={(data.business_digest.stats.ai_sessions || 0).toLocaleString()} />
                <Row label="New users" value={(data.business_digest.stats.new_users || 0).toLocaleString()} />
              </div>
            )}
            <Button onClick={() => sendNow("business")} disabled={busy === "business"} variant="outline" size="sm" className="w-full rounded-full" data-testid="digest-send-business">
              {busy === "business" ? <><Loader2 className="size-3 animate-spin" /> Sending…</> : <><Mail className="size-3" /> Send business digest now</>}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
